import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/hooks/use-auth";
import { ThemeProvider } from "@/contexts/theme-context";
import ThemeApplier from "@/components/ThemeApplier";
import { DynamicHead } from "@/components/DynamicHead";
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminHome from "@/pages/admin/AdminHome";
import AdminProjetos from "@/pages/admin/AdminProjetos";
import AdminServicos from "@/pages/admin/AdminServicos";
import AdminClientes from "@/pages/admin/AdminClientes";
import AdminDepoimentos from "@/pages/admin/AdminDepoimentos";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminContato from "@/pages/admin/AdminContato";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminContent from "@/pages/admin/AdminContent";
import ProjetoForm from "@/pages/admin/ProjetoForm";
import ServicoForm from "@/pages/admin/ServicoForm";
import ClienteForm from "@/pages/admin/ClienteForm";
import DepoimentoForm from "@/pages/admin/DepoimentoForm";
import Login from "@/pages/admin/Login";
import Index from "@/pages/Index";
import Projetos from "@/pages/Projetos";
import ProjetoDetalhe from "@/pages/ProjetoDetalhe";
import Servicos from "@/pages/Servicos";
import Sobre from "@/pages/Sobre";
import Contato from "@/pages/Contato";
import Desenvolvimento from "@/pages/Desenvolvimento";
import DemoOne from "@/pages/DemoOne";
import NotFound from "@/pages/NotFound";
import { useState, useEffect } from "react";
import GlobalLoader from "@/components/GlobalLoader";

const queryClient = new QueryClient();

const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time for the animation intro (approx 2.5s)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2800); // slightly longer than 2.5s to be safe

    return () => clearTimeout(timer);
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ThemeProvider>
            <AuthProvider>
              <Toaster />
              <DynamicHead />
              {isLoading ? (
                <GlobalLoader />
              ) : (
                <BrowserRouter>
                <AnalyticsTracker />
                <ScrollToTop />
                <Routes>
                  {/* Rotas Públicas */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Index />} />
                    <Route path="projetos" element={<Projetos />} />
                    <Route path="projetos/:slug" element={<ProjetoDetalhe />} />
                    <Route path="servicos" element={<Servicos />} />
                    <Route path="sobre" element={<Sobre />} />
                    <Route path="contato" element={<Contato />} />
                    <Route path="desenvolvimento" element={<Desenvolvimento />} />
                    <Route path="demo-sidebar" element={<DemoOne />} />
                  </Route>

                  {/* Rotas Admin */}
                  <Route path="/admin/login" element={<Login />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminHome />} />
                    <Route path="projetos" element={<AdminProjetos />} />
                    <Route path="projetos/novo" element={<ProjetoForm />} />
                    <Route path="projetos/:id/editar" element={<ProjetoForm />} />
                    <Route path="servicos" element={<AdminServicos />} />
                    <Route path="servicos/novo" element={<ServicoForm />} />
                    <Route path="servicos/:id/editar" element={<ServicoForm />} />
                    <Route path="clientes" element={<AdminClientes />} />
                    <Route path="clientes/novo" element={<ClienteForm />} />
                    <Route path="clientes/:id/editar" element={<ClienteForm />} />
                    <Route path="depoimentos" element={<AdminDepoimentos />} />
                    <Route path="depoimentos/novo" element={<DepoimentoForm />} />
                    <Route path="depoimentos/:id/editar" element={<DepoimentoForm />} />
                    <Route path="configuracoes" element={<AdminSettings />} />
                    <Route path="conteudo" element={<AdminContent />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="contato" element={<AdminContato />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
              )}
            </AuthProvider>
          </ThemeProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
